import { type ReactNode, useMemo, useState } from "react";
import { ChevronDown, Clock, Tag, User } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ExpeditionPreFinalizeDetails } from "@/components/backOffice/logs/expeditionPreFinalizeDetails";
import { JsonTree } from "@/components/shared/jsonTree";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useLogTemplateTexts } from "@/hooks/useLogTemplateTexts";
import { cn } from "@/lib/utils";
import {
  DATE_PLACEHOLDER,
  DiffEntry,
  computeDiff,
  extractProminent,
  formatDateSafe,
  formatDiffValue,
  formatJson,
  omitProminentKeys,
  stripUpdatedAt,
} from "@/lib/logFormat";

export type LogDetailEntry = {
  action: string;
  entityType: string;
  createdAt: string | Date;
  user?: { firstName: string; lastName: string } | null;
  details?: unknown;
  previousValue?: unknown;
  newValues?: unknown;
};

type LogDetailProps = {
  log: LogDetailEntry;
};

export const LogDetail = ({ log }: LogDetailProps) => {
  const { t } = useTranslation("boLogs");
  const { getTitle, getDescription, getEntityLabel } = useLogTemplateTexts();
  const description = getDescription(log.action);
  const prominent = extractProminent(log.details);
  const detailsRest = omitProminentKeys(log.details);
  const hasDiff = log.previousValue != null || log.newValues != null;
  const diffEntries = useMemo(
    () => computeDiff(log.previousValue, log.newValues),
    [log.previousValue, log.newValues],
  );

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="text-base font-semibold text-gray-900">{getTitle(log.action)}</h3>
        {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
      </div>

      {prominent && (
        <div
          className={cn(
            "rounded-lg border px-4 py-3 text-sm font-medium",
            prominent.isError
              ? "border-red-200 bg-red-50 text-red-700"
              : "border-blue-100 bg-blue-50 text-blue-700",
          )}
        >
          {prominent.text}
        </div>
      )}

      <div className="flex flex-col gap-2 rounded-lg border border-gray-100 bg-gray-50 p-4">
        <LogDetailRow
          icon={<Tag className="size-3.5" />}
          label={t("logDetail.actionLabel")}
          value={log.action}
        />
        <LogDetailRow
          icon={<Tag className="size-3.5" />}
          label={t("logDetail.entityTypeLabel")}
          value={getEntityLabel(log.entityType)}
        />
        <LogDetailRow
          icon={<Clock className="size-3.5" />}
          label={t("logDetail.dateLabel")}
          value={formatDateSafe(log.createdAt)}
        />
        <LogDetailRow
          icon={<User className="size-3.5" />}
          label={t("logDetail.userLabel")}
          value={log.user ? `${log.user.firstName} ${log.user.lastName}` : DATE_PLACEHOLDER}
        />
      </div>

      {hasDiff && (
        <LogDiff entries={diffEntries} previous={log.previousValue} next={log.newValues} />
      )}

      {log.action === "EXPEDITION_PRE_FINALIZE" && (
        <ExpeditionPreFinalizeDetails details={log.details} />
      )}

      {detailsRest != null && <LogDetailsCollapsible value={detailsRest} />}
    </div>
  );
};

type LogDetailRowProps = {
  icon: ReactNode;
  label: string;
  value: string;
};

export const LogDetailRow = ({ icon, label, value }: LogDetailRowProps) => (
  <div className="flex flex-col items-start text-sm sm:flex-row sm:gap-4">
    <span className="flex w-28 flex-shrink-0 gap-2 font-medium text-gray-600">
      <span className="mt-0.5 flex-shrink-0 text-gray-400">{icon}</span>
      {label}
    </span>
    <span className="w-full text-end text-gray-700 sm:w-auto sm:text-start">{value}</span>
  </div>
);

type LogDetailsCollapsibleProps = {
  value: unknown;
};

export const LogDetailsCollapsible = ({ value }: LogDetailsCollapsibleProps) => {
  const [open, setOpen] = useState(true);
  const { t } = useTranslation("boLogs");
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger
        className={cn(
          "flex w-full items-center justify-between bg-gray-50 px-3 py-2 text-sm font-medium text-gray-600 ring-1 ring-gray-200 transition-colors hover:bg-gray-100",
          open ? "rounded-t-md" : "rounded-md",
        )}
      >
        {t("logDetail.technicalDetail")}
        <ChevronDown
          className={cn(
            "size-4 text-gray-400 transition-transform duration-200",
            open && "rotate-180",
          )}
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="h-full">
          <JsonTree value={value} className="-mt-px h-full rounded-t-none" />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

type LogDiffProps = {
  entries: DiffEntry[];
  previous: unknown;
  next: unknown;
};

const LogDiff = ({ entries, previous, next }: LogDiffProps) => {
  const { t } = useTranslation("boLogs");
  const bothAreObjects =
    previous != null &&
    next != null &&
    typeof previous === "object" &&
    !Array.isArray(previous) &&
    typeof next === "object" &&
    !Array.isArray(next);

  if (!bothAreObjects && (previous != null || next != null)) {
    return (
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium text-gray-600">{t("logDetail.changes")}</p>
        <div className="overflow-hidden rounded-md border border-gray-200 font-mono text-xs">
          {previous != null && (
            <div className="flex gap-2 bg-red-50 px-3 py-2 text-red-700">
              <span className="text-red-400 select-none">−</span>
              <pre className="break-words whitespace-pre-wrap">
                {formatJson(stripUpdatedAt(previous))}
              </pre>
            </div>
          )}
          {next != null && (
            <div className="flex gap-2 bg-green-50 px-3 py-2 text-green-700">
              <span className="text-green-400 select-none">+</span>
              <pre className="break-words whitespace-pre-wrap">
                {formatJson(stripUpdatedAt(next))}
              </pre>
            </div>
          )}
        </div>
      </div>
    );
  }

  const visibleEntries = entries.filter((e) => e.key !== "updatedAt");
  if (visibleEntries.length === 0) return null;

  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium text-gray-600">{t("logDetail.changes")}</p>
      <div className="overflow-hidden rounded-md border border-gray-200 font-mono text-xs">
        {visibleEntries.map((entry) => (
          <div key={entry.key}>
            {(entry.type === "removed" || entry.type === "changed") && (
              <div className="flex items-baseline gap-2 bg-red-50 px-3 py-1 text-red-700">
                <span className="text-red-400 select-none">−</span>
                <span className="font-semibold">{entry.key}:</span>
                <span className="min-w-0 break-all text-red-600">
                  {formatDiffValue(entry.oldValue)}
                </span>
              </div>
            )}
            {(entry.type === "added" || entry.type === "changed") && (
              <div className="flex items-baseline gap-2 bg-green-50 px-3 py-1 text-green-700">
                <span className="text-green-400 select-none">+</span>
                <span className="font-semibold">{entry.key}:</span>
                <span className="min-w-0 break-all text-green-600">
                  {formatDiffValue(entry.newValue)}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
